import React from 'react';
import { Link } from 'react-router-dom';
import { useConvexBanners } from '../../services/convexService';
import { useLanguage } from '../../contexts/LanguageContext';

interface BannerData {
  ar: string;
  en: string;
  fr: string;
  image?: string;
  type: 'text' | 'image';
  link?: string;
}

const AdBanner: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const { getBanners } = useConvexBanners();
  const banners = getBanners();

  // Don't render if loading or no banners
  if (banners === undefined || banners.length === 0) return null;

  // Render banner card
  const renderBannerCard = (banner: BannerData, key: string | number) => {
    const content = banner.type === 'text'
      ? (banner[language as keyof BannerData] || banner.ar || '')
      : banner.image;

    if (banner.type === 'text') {
      const hasLink = !!banner.link?.trim();
      const isInternalLink = hasLink && banner.link.startsWith('/');
      const isExternalLink = hasLink && (banner.link.startsWith('http://') || banner.link.startsWith('https://'));

      const textContent = (
        <span className="text-[0.65rem] sm:text-xs font-semibold text-white whitespace-nowrap">
          {content}
        </span>
      );

      if (isInternalLink) {
        return (
          <div key={key} className="flex-shrink-0 px-3 sm:px-4 inline-flex items-center">
            <Link to={banner.link} className="hover:underline cursor-pointer">
              {textContent}
            </Link>
          </div>
        );
      } else if (isExternalLink) {
        return (
          <div key={key} className="flex-shrink-0 px-3 sm:px-4 inline-flex items-center">
            <a href={banner.link} target="_blank" rel="noopener noreferrer" className="hover:underline cursor-pointer">
              {textContent}
            </a>
          </div>
        );
      } else {
        return (
          <div key={key} className="flex-shrink-0 px-3 sm:px-4 inline-flex items-center">
            {textContent}
          </div>
        );
      }
    } else if (banner.type === 'image') {
      const hasLink = !!banner.link?.trim();

      const imgElement = (
        <img
          src={banner.image}
          alt=""
          className="h-6 sm:h-7 object-contain"
        />
      );

      if (hasLink) {
        const isInternalLink = banner.link.startsWith('/');
        const isExternalLink = banner.link.startsWith('http');

        if (isInternalLink) {
          return (
            <div key={key} className="flex-shrink-0 px-3 sm:px-4 inline-flex items-center">
              <Link to={banner.link}>{imgElement}</Link>
            </div>
          );
        } else if (isExternalLink) {
          return (
            <div key={key} className="flex-shrink-0 px-3 sm:px-4 inline-flex items-center">
              <a href={banner.link} target="_blank" rel="noopener noreferrer">{imgElement}</a>
            </div>
          );
        }
      }

      return (
        <div key={key} className="flex-shrink-0 px-3 sm:px-4 inline-flex items-center">
          {imgElement}
        </div>
      );
    }

    return null;
  };

  return (
    <div
      data-banner
      className="w-full h-full flex items-center text-white"
      dir={isRTL ? 'rtl' : 'ltr'}

    >
      <div className="relative w-full h-full">
        <div className="flex items-center h-full">
          {/* Horizontal Scrolling Banner Content - SMALLER TEXT AND FITTED */}
          <div className="flex-1 h-full overflow-hidden">
            <div className="relative h-full overflow-hidden">
              <div
                className={`absolute inset-y-0 flex items-center whitespace-nowrap gap-6 h-full
                  ${isRTL ? 'animate-banner-scroll-rtl' : 'animate-banner-scroll-ltr'}
                `}
                style={{ top: '20%', transform: 'translateY(-20%)' }}
              >
                {/* Render all banners */}
                {banners.map((banner, index) => renderBannerCard(banner, index))}
                {/* Duplicate banners for seamless loop */}
                {banners.map((banner, index) => renderBannerCard(banner, `duplicate-${index}`))}
                {/* Triple banners for single card scenarios */}
                {banners.length === 1 && banners.map((banner, index) => renderBannerCard(banner, `triple-${index}`))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdBanner;